import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { selectReelCharacters } from '../../utils/selectReelCharacters';
import { useLearner } from '../../hooks/useLearner';
import audioManager from '../../lib/audioManager';
import ReviewCard from './ReviewCard';
import ReviewProgress from './ReviewProgress';
import BuddyCelebration from './BuddyCelebration';
import s from './ReviewReel.module.css';

export default function ReviewReel({ characters, onComplete }) {
  const { t } = useTranslation();
  const { data: activeLearner } = useLearner();
  const deck = useMemo(() => selectReelCharacters(characters), [characters]);

  const [index, setIndex] = useState(0);
  const [hardIds, setHardIds] = useState([]);
  const [buddyVisible, setBuddyVisible] = useState(false);

  const total = deck.length;
  const current = deck[index] || null;

  // Empty-deck edge case: nothing eligible to review. Fire onComplete async so
  // the parent can route past the reel without us touching state during render.
  useEffect(() => {
    if (total === 0) onComplete?.([]);
  }, [total, onComplete]);

  const advance = useCallback((nextHardIds) => {
    const nextIndex = index + 1;
    if (nextIndex >= total) {
      audioManager.playSFX('reel-complete');
      onComplete?.(nextHardIds);
      return;
    }
    setIndex(nextIndex);
  }, [index, total, onComplete]);

  // Swipe right = "got it" → affirming swipe sound, buddy plays, then advance on
  // its exit (the buddy-celebration-pop fires from BuddyCelebration itself).
  const handleSwipeRight = useCallback(() => {
    audioManager.playSFX('review-swipe-positive');
    setBuddyVisible(true);
  }, []);

  // Buddy finished its full in/hold/out cycle.
  const handleBuddyExit = useCallback(() => {
    setBuddyVisible(false);
    advance(hardIds);
  }, [advance, hardIds]);

  // Swipe left = "still hard" → neutral swipe sound (not a failure), record id,
  // advance without buddy.
  const handleSwipeLeft = useCallback((id) => {
    audioManager.playSFX('review-swipe-neutral');
    const next = hardIds.includes(id) ? hardIds : [...hardIds, id];
    setHardIds(next);
    advance(next);
  }, [hardIds, advance]);

  if (total === 0) return null;

  return (
    <div className={s.reel}>
      <div className={s.progressBar}>
        <ReviewProgress current={Math.min(index + 1, total)} total={total} />
      </div>

      <div className={s.cardRow}>
        <button
          type="button"
          className={`${s.actionBtn} ${s.actionBtnLeft}`}
          onClick={() => current && handleSwipeLeft(current.id)}
          aria-label={t('review.stillHard')}
        >
          {t('review.stillHard')}
        </button>

        <div className={s.stage}>
          <AnimatePresence mode="wait">
            {current && (
              <ReviewCard
                key={current.id}
                character={current}
                onSwipeRight={handleSwipeRight}
                onSwipeLeft={handleSwipeLeft}
              />
            )}
          </AnimatePresence>

          <BuddyCelebration
            visible={buddyVisible}
            onExitComplete={handleBuddyExit}
            buddySlug={activeLearner?.buddy?.slug}
          />
        </div>

        <button
          type="button"
          className={`${s.actionBtn} ${s.actionBtnRight}`}
          onClick={handleSwipeRight}
          aria-label={t('review.gotIt')}
        >
          {t('review.gotIt')}
        </button>
      </div>
    </div>
  );
}
